import { useEffect, useRef } from 'react';

import {
  Animated,
  Easing,
  StyleSheet,
  View,
} from 'react-native';

import { colors } from '../theme';

export type AvailabilityIndicatorProps = {
  size?: number;
};

const ANIMATION_DURATION_MS = 1800;
const SECOND_RING_DELAY_MS = 900;

const createPulseLoop = (value: Animated.Value, delayMs: number) =>
  Animated.loop(
    Animated.sequence([
      Animated.delay(delayMs),
      Animated.timing(value, {
        duration: ANIMATION_DURATION_MS,
        easing: Easing.out(Easing.cubic),
        toValue: 1,
        useNativeDriver: true,
      }),
      Animated.timing(value, {
        duration: 0,
        toValue: 0,
        useNativeDriver: true,
      }),
    ]),
  );

const ringAnimatedStyle = (progress: Animated.Value) => ({
  opacity: progress.interpolate({
    inputRange: [0, 0.16, 0.66, 1],
    outputRange: [0, 0.5, 0, 0],
  }),
  transform: [
    {
      scale: progress.interpolate({
        inputRange: [0, 1],
        outputRange: [1, 1.85],
      }),
    },
  ],
});

const dotAnimatedStyle = (progress: Animated.Value) => ({
  opacity: progress.interpolate({
    inputRange: [0, 0.5, 1],
    outputRange: [0.88, 1, 0.88],
  }),
  transform: [
    {
      scale: progress.interpolate({
        inputRange: [0, 0.5, 1],
        outputRange: [1, 1.16, 1],
      }),
    },
  ],
});

export function AvailabilityIndicator({ size = 6 }: AvailabilityIndicatorProps) {
  const progressPrimary = useRef(new Animated.Value(0)).current;
  const progressSecondary = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    progressPrimary.setValue(0);
    progressSecondary.setValue(0);

    const primaryLoop = createPulseLoop(progressPrimary, 0);
    const secondaryLoop = createPulseLoop(progressSecondary, SECOND_RING_DELAY_MS);

    primaryLoop.start();
    secondaryLoop.start();

    return () => {
      primaryLoop.stop();
      secondaryLoop.stop();
    };
  }, [progressPrimary, progressSecondary]);

  const ringSize = size + 8;

  return (
    <View style={[styles.wrapper, { height: size, width: size }]}>
      <Animated.View
        pointerEvents="none"
        style={[
          styles.ring,
          {
            borderColor: colors.green,
            borderRadius: ringSize / 2,
            height: ringSize,
            width: ringSize,
          },
          ringAnimatedStyle(progressPrimary),
        ]}
      />

      <Animated.View
        pointerEvents="none"
        style={[
          styles.ring,
          {
            borderColor: colors.green,
            borderRadius: ringSize / 2,
            height: ringSize,
            width: ringSize,
          },
          ringAnimatedStyle(progressSecondary),
        ]}
      />

      <Animated.View
        style={[
          styles.dot,
          {
            backgroundColor: colors.green,
            borderRadius: size / 2,
            height: size,
            width: size,
          },
          dotAnimatedStyle(progressPrimary),
        ]}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'visible',
  },
  ring: {
    borderWidth: 1,
    position: 'absolute',
  },
  dot: {
    // size + color injected
  },
});
